import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from 'src/api/request-handler';
import { API_GET_ADDRESS_SUGGESTIONS } from 'src/api/request-objects';
import { stateCodeToFullName } from 'src/utils/state-mapper';

export type Suggestion = {
  street_line: string;
  secondary: string;
  city: string;
  state: string;
  zipcode: string;
  entries: number;
};

export type FPAddressFormFields = {
  address_number: string;
  address_street_name: string;
  apt_suite: string;
  city: string;
  state_name: string;
  zip_code: string;
};

export const NO_ADDRESS_FOUND_MESSAGE = 'No address found? CLICK HERE TO ENTER MANUALLY.';

export const TYPE_TO_SEARCH_MESSAGE = 'Type to search for an address.';

export const AUTOCOMPLETE_KEY_FIELD = 'address_number';

function buildAddress(suggestion: Suggestion) {
  let whiteSpace = '';
  if (suggestion.secondary) {
    if (suggestion.entries > 1) {
      suggestion.secondary += ' (' + suggestion.entries + ' entries)';
    }
    whiteSpace = ' ';
  }
  return (
    suggestion.street_line +
    whiteSpace +
    suggestion.secondary +
    ' ' +
    suggestion.city +
    ', ' +
    suggestion.state +
    ' ' +
    suggestion.zipcode
  );
}

export function useAddressSuggestions(searchAddress: string, searchByEmail: string) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const [hasSearched, setHasSearched] = useState(false);

  const placeholderText = [hasSearched ? NO_ADDRESS_FOUND_MESSAGE : TYPE_TO_SEARCH_MESSAGE];

  const suggestionsMapped = useMemo(
    () => (suggestions.length > 0 ? suggestions.map((suggestion) => buildAddress(suggestion)) : placeholderText),
    [suggestions],
  );

  const resetSuggestions = () => {
    setSuggestions([]);
    setHasSearched(false);
  };

  useEffect(() => {
    if (searchAddress.length === 0) {
      resetSuggestions();
    }
  }, [searchAddress]);

  useQuery({
    queryKey: ['address-suggestions', searchAddress, searchByEmail],
    queryFn: async () => {
      if (searchAddress.length === 0) {
        setSuggestions([]);
        return [];
      }

      const suggestionsFound = await apiRequest<Suggestion[]>(API_GET_ADDRESS_SUGGESTIONS, {
        query: {
          search: searchAddress,
          email: searchByEmail
        },
      });

      setSuggestions(suggestionsFound);

      setHasSearched(true);

      return suggestionsFound;
    },
    initialData: [],
    staleTime: 0, // Data immediately considered stale
    gcTime: 0,
  });

  const getAddressFormFieldsFromOption = (option: string): FPAddressFormFields | null => {
    const optionIndex = suggestionsMapped.findIndex((suggestion) => suggestion === option);

    const suggestion = suggestions[optionIndex];

    if (!suggestion) {
      // Adding this because if the user deletes the address, then it looks like the user can submit without values
      return {
        address_number: '',
        address_street_name: '',
        apt_suite: '',
        city: '',
        state_name: '',
        zip_code: '',
      };
    }

    // check if suggestion.secondary matches something like Apt (100 entries)
    const aptSuiteMatch = suggestion.secondary.match(/\((\d+) entries\)/);
    if (aptSuiteMatch) {
      return {
        address_number: suggestion.street_line.split(' ')[0],
        address_street_name: suggestion.street_line.split(' ').slice(1).join(' '),
        apt_suite: suggestion.secondary.slice(0, aptSuiteMatch.index),
        city: '',
        state_name: '',
        zip_code: '',
      };
    }

    return {
      address_number: suggestion.street_line.split(' ')[0],
      address_street_name: suggestion.street_line.split(' ').slice(1).join(' '),
      apt_suite: suggestion.secondary,
      city: suggestion.city,
      state_name: stateCodeToFullName(suggestion.state),
      zip_code: suggestion.zipcode,
    };
  };

  return {
    suggestions: suggestionsMapped,
    resetSuggestions,
    getAddressFormFieldsFromOption,
  };
}
